/* globals sportRESummaryUrl sportREChildrenUrl sportRESearchUrl venuesIndexUrl vikEnabled reEventId costTemplateEnabled costPackEnabled */

import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import RESummaryCards from 'Components/revenues_expenditures/RESummaryCards';
import RETypeIconLegend from 'Components/revenues_expenditures/RETypeIconLegend';
import RevenuesExpendituresTable from 'Components/revenues_expenditures/RevenuesExpendituresTable';

const REVENUE = 'revenue';
const EXPENDITURE = 'expenditure';

/**
 * Loads the tab summary-first: group rows up front, a group's lines on expand,
 * and search on the server. `meta.node_path` is sent back verbatim — group
 * names are locale-dependent, so the client never rebuilds a path itself.
 */
function SportRevenuesExpenditures({ summaryUrl, childrenUrl, searchUrl, venuesIndexUrl, vikEnabled, eventId, costTemplateEnabled, costPackEnabled }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!summaryUrl) return;

        setLoading(true);
        axios
            .get(summaryUrl)
            .then(({ data: response }) => {
                setData(response.data || response);
            })
            .catch(() => {
                setError($t('Failed to load revenues and expenditures.'));
            })
            .finally(() => setLoading(false));
    }, [summaryUrl]);

    const fetchChildren = useCallback(
        (side, path) => axios.get(childrenUrl, { params: { side, path } }).then(({ data: response }) => response.data || []),
        [childrenUrl],
    );
    const fetchSearch = useCallback(
        (side, q, signal) => axios.get(searchUrl, { params: { side, q }, signal }).then(({ data: response }) => response.data || []),
        [searchUrl],
    );

    const loadRevenueChildren = useCallback((node) => fetchChildren(REVENUE, node.meta?.node_path ?? []), [fetchChildren]);
    const loadExpenditureChildren = useCallback((node) => fetchChildren(EXPENDITURE, node.meta?.node_path ?? []), [fetchChildren]);
    const loadAllRevenue = useCallback(() => fetchChildren(REVENUE, []), [fetchChildren]);
    const loadAllExpenditure = useCallback(() => fetchChildren(EXPENDITURE, []), [fetchChildren]);
    const searchRevenue = useCallback((q, signal) => fetchSearch(REVENUE, q, signal), [fetchSearch]);
    const searchExpenditure = useCallback((q, signal) => fetchSearch(EXPENDITURE, q, signal), [fetchSearch]);

    if (error) {
        return <div className="tw-p-4 tw-text-red-500">{error}</div>;
    }

    const revenueTotals = { cash: null, vik: null, total: data?.totals?.revenue ?? null };
    // expenditure_totals carries the leaf-summed cash/vik/total so the footer can
    // foot the Cash/VIK columns the event view now shows; fall back to the scalar
    // expenditure total on caches served before this shipped.
    const expenditureTotals = data?.expenditure_totals ?? { cash: null, vik: null, total: data?.totals?.expenditure ?? null };

    return (
        <div className="tw-space-y-3 tw-mt-6">
            <div className="tw-rounded-lg tw-border-2 tw-border-grey tw-bg-grey/20 tw-px-4 tw-py-3 tw-text-sm tw-text-charcoal-75">
                {$t('Event allocation is managed at the venue.')}{' '}
                {venuesIndexUrl && (
                    <a href={venuesIndexUrl} className="tw-font-semibold tw-text-blue" data-action="navigate">
                        {$t('Manage allocations by venue')}
                    </a>
                )}
            </div>

            <RESummaryCards context="event" totals={data?.totals ?? null} committedTotals={data?.committed_totals ?? null} loading={loading} />

            <RevenuesExpendituresTable
                title={$t('Revenue')}
                context="event"
                isRevenue
                eventId={eventId}
                loading={loading}
                nodes={data?.revenue_tree ?? []}
                totals={revenueTotals}
                vikEnabled={vikEnabled}
                loadChildren={loadRevenueChildren}
                onSearch={searchRevenue}
                loadAll={loadAllRevenue}
            />

            <RevenuesExpendituresTable
                title={$t('Expenditure')}
                context="event"
                eventId={eventId}
                loading={loading}
                nodes={data?.expenditure_tree ?? []}
                totals={expenditureTotals}
                vikEnabled={vikEnabled}
                commitmentLayerEnabled
                committedTotals={data?.committed_totals ?? null}
                loadChildren={loadExpenditureChildren}
                onSearch={searchExpenditure}
                loadAll={loadAllExpenditure}
            />

            <RETypeIconLegend showTemplateKey={costTemplateEnabled} showPackKey={costPackEnabled} />
        </div>
    );
}

export default SportRevenuesExpenditures;

if (document.getElementById('SportRevenuesExpenditures')) {
    var rootEl = document.getElementById('SportRevenuesExpenditures');
    ReactDOM.render(
        <App>
            <SportRevenuesExpenditures
                summaryUrl={sportRESummaryUrl}
                childrenUrl={sportREChildrenUrl}
                searchUrl={sportRESearchUrl}
                venuesIndexUrl={typeof venuesIndexUrl !== 'undefined' ? venuesIndexUrl : null}
                vikEnabled={typeof vikEnabled !== 'undefined' ? vikEnabled : false}
                eventId={typeof reEventId !== 'undefined' ? reEventId : null}
                costTemplateEnabled={typeof costTemplateEnabled !== 'undefined' ? costTemplateEnabled : false}
                costPackEnabled={typeof costPackEnabled !== 'undefined' ? costPackEnabled : false}
            />
        </App>,
        rootEl,
    );
}
